<script setup lang="ts">
import StepperItem from './onlineSurvey/StepperItem.vue'
import FinishForm from './onlineSurvey/FinishTypes/FinishForm.vue'
import FinishRedirect from './onlineSurvey/FinishTypes/FinishRedirect.vue'
import FinishMessage from './onlineSurvey/FinishTypes/FinishMessage.vue'
import ProgressBar from './onlineSurvey/questionTypes/ProgressBar.vue'

import { useSurvey } from '@/composables/onlineSurvey/useSurvey'
import type { BaseWebComponentProps } from '../types/webComponents'
import { ref, computed, onMounted } from 'vue'

const n8nResult = ref<any>(null)
const submittingData = ref(false)
const error = ref(false)
const loadingError = ref(false)
const isLoading = ref(true)

interface Props extends BaseWebComponentProps {
  theme?: 'light' | 'dark'
  primaryColor?: string
  secondaryColor?: string
  surveyId?: string
}

// Props mit Standardwerten
const props = withDefaults(defineProps<Props>(), {
  theme: 'light',
  primaryColor: '#108cc7',
  secondaryColor: '#cf4a38',
  surveyId: '6172a886-5a4c-4a8c-9317-5f890caad362',
})

const {
  loadSurvey,
  surveyConfig,
  currentStep,
  answers,
  visibleQuestionIds,
  cleanupInvisibleAnswers,
} = useSurvey()
const finished = ref(false)

onMounted(() => {
  initSurvey()
})

async function initSurvey() {
  try {
    isLoading.value = true

    // URL basierend auf Umgebung wählen
    let surveyUrl = `${import.meta.env.VITE_API_BASE_URL}surveys/${props.surveyId}`

    console.log('Fetching survey from URL:', surveyUrl)

    let response

    response = await fetch(surveyUrl, { credentials: 'omit' })

    console.log('Fetching survey from URL:', response)
    const surveyResponse = await response.json()
    console.log('Survey response received:', surveyResponse)

    // Prüfen ob die Daten im erwarteten Format vorliegen
    let surveyArray
    let surveyMeta
    if (surveyResponse.data) {
      // Neue Struktur: response.data direkt
      surveyArray = surveyResponse.data.surveyArray
    }
    // Sicherstellen, dass surveyData ein Objekt und kein String ist
    if (typeof surveyArray === 'string') {
      surveyArray = JSON.parse(surveyArray)
    }

    surveyMeta = {
      n8nEndpoint: surveyResponse.data.n8nEndpoint ?? null,
      finish: {
        type: surveyResponse.data.finishType ?? null,
      },
      finishConfig: surveyResponse.data.finishConfig ?? null,
      benefitText: surveyResponse.data.benefitText ?? null,
      aiPrompt: surveyResponse.data.aiPrompt ?? null,
    }

    loadSurvey(surveyArray, surveyMeta)
    loadingError.value = false
  } catch (e) {
    console.error('Could not load survey config from remote URL:', e)
    loadingError.value = true
  } finally {
    isLoading.value = false
    cleanupInvisibleAnswers()
  }
}

async function handleSurveyCompletion() {
  console.log('Survey completed, preparing to post data...', surveyConfig.value.finish.type)
  if (surveyConfig.value && surveyConfig.value.finish.type !== 'formular') {
    await postData()
  }
  finished.value = true
}

async function postData() {
  console.log('Posting survey data to endpoint...')
  const payload = {
    answers: answers.value,
    surveyId: props.surveyId,
    aiPrompt: surveyConfig.value?.aiPrompt || null,
    benefitText: surveyConfig.value?.benefitText || null,
  }

  try {
    submittingData.value = true
    const endPoint =
      surveyConfig.value?.n8nEndpoint ??
      'https://n8n.simki.cloud/webhook/278d8901-f8d9-49b5-9cc5-aa3a6e94bbd7'
    const result = await fetch(endPoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!result.ok) throw new Error(`HTTP error! status: ${result.status}`)

    // Versuche JSON zu parsen, falls Response Content-Type JSON ist
    let responseData
    const contentType = result.headers.get('content-type')

    if (contentType && contentType.includes('application/json')) {
      responseData = await result.json()
      console.log('Server response (JSON):', responseData)
      n8nResult.value = responseData
    } else {
      // Fallback für Text/HTML Response
      responseData = await result.text()
      console.log('Server response (Text):', responseData)
      n8nResult.value = responseData
    }
  } catch (e) {
    console.error('Error posting survey data:', e)
    // Fallback-Response für Fehlerbehandlung
    n8nResult.value = {
      message: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
    }
    error.value = true
  } finally {
    submittingData.value = false
  }
}

const answeredQuestions = ref(0)

function updateAnsweredCount() {
  answeredQuestions.value = answers.value.length
}
</script>
<template>
  <!-- Loading State -->
  <div v-if="isLoading" class="online-survey" :class="`theme-${theme}`">
    <div class="p-6 has-text-centered">
      <div class="button is-primary is-loading is-large">Survey wird geladen...</div>
    </div>
  </div>

  <!-- Error State -->
  <div v-else-if="loadingError" class="online-survey" :class="`theme-${theme}`">
    <div class="p-4">
      <div class="notification is-danger">
        <strong>Fehler beim Laden der Survey</strong>
        <p>
          Die Survey konnte nicht geladen werden. Bitte versuchen Sie es später erneut oder
          kontaktieren Sie den Support.
        </p>
      </div>
    </div>
  </div>

  <!-- Survey Content - nur wenn erfolgreich geladen -->
  <div
    v-else-if="surveyConfig && !loadingError"
    class="online-survey"
    :class="`theme-${theme}`"
    :style="{ '--primary-color': primaryColor, '--secondary-color': secondaryColor }"
  >
    <header
      class="is-flex is-flex-direction-column is-justify-content-center is-align-items-center px-4 py-4 has-text-centered"
    >
      <div v-if="currentStep == 0">
        <h1 class="is-size-4-desktop is-size-5-tablet has-text-weight-semibold">
          {{ surveyConfig.title }}
        </h1>
        <p class="py-4">{{ surveyConfig.description }}</p>
      </div>
      <ProgressBar
        v-if="!finished"
        :answeredQuestions="answeredQuestions"
        :totalQuestions="visibleQuestionIds.size"
      />
    </header>

    <div
      v-for="(step, index) in surveyConfig.steps"
      :key="step.id"
      class="survey-step"
      v-if="!submittingData"
    >
      <StepperItem
        v-if="index === currentStep && !finished"
        :step="step"
        @surveryCompleted="handleSurveyCompletion"
        @moveSteps="updateAnsweredCount"
        class="p-4"
      />
    </div>

    <!-- Post-Submit Error -->
    <div v-if="error && finished" class="p-4">
      <div class="notification">
        <strong>Fehler beim Senden der Daten:</strong>
        <p>
          {{
            n8nResult?.message ||
            'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
          }}
        </p>
      </div>
    </div>
    <!-- Submitting State -->
    <div v-if="submittingData" class="p-6 has-text-centered">
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p class="mt-4 is-size-5 has-text-weight-medium">Eingaben werden analysiert...</p>
        <p class="is-size-7 has-text-grey">Bitte warten Sie einen Moment</p>
      </div>
    </div>
    <!-- Finish States -->
    <div v-else-if="!submittingData && finished && !error" class="p-4">
      <FinishRedirect
        v-if="surveyConfig.finish.type === 'redirect'"
        :redirectUrl="surveyConfig.finishConfig ?? ''"
      />
      <FinishMessage
        v-else-if="surveyConfig.finish.type === 'message'"
        :message="n8nResult?.output ?? 'Vielen Dank für Ihre Teilnahme!'"
      />
      <FinishForm
        v-else-if="surveyConfig.finish.type === 'formular'"
        @formCompleted="postData"
        :uuid="surveyConfig.finishConfig ?? ''"
        :theme="theme"
        :primaryColor="primaryColor"
        :message="n8nResult?.output"
      />
    </div>
  </div>
</template>

<style>
/* Primary Color Override mit CSS Custom Properties */
.button.is-primary {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  color: white !important;
}

.button.is-loading {
  color: transparent !important;
}

.button.is-primary:hover:not(:disabled) {
  background-color: color-mix(in srgb, var(--primary-color) 85%, black) !important;
  border-color: color-mix(in srgb, var(--primary-color) 85%, black) !important;
}

.button.is-primary:focus {
  box-shadow: 0 0 0 0.125em color-mix(in srgb, var(--primary-color) 25%, transparent) !important;
}

/* Weißer Spinner für Primary Button */
.button.is-primary.is-loading::after {
  border-color: white !important;
  border-right-color: transparent !important;
  border-top-color: transparent !important;
}

/* Custom Loading Spinner */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--bulma-border, #e0e0e0);
  border-radius: 50%;
  border-top-color: var(--primary-color, #3273dc);
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Loading Spinner */
@media (max-width: 768px) {
  .loading-spinner {
    width: 40px;
    height: 40px;
    border-width: 3px;
  }

  .loading-container {
    min-height: 150px;
    border-top-color: transparent !important;
  }
}
</style>
